import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useFetchTrigger } from '../../lib/useFetchTrigger';
import type { PayMark } from '@bbbk/schema';

// PayMark (GET /api/pay-marks) is single-sourced in @bbbk/schema; re-exported
// here so this hook's consumers keep importing it from './usePayMarks'.
export type { PayMark };

// Pay marks for a date window (the timesheet's weeks). Mark/unmark write to the
// API from the view; reload() refetches. Holds the last set across a window
// change, revalidates on return — same shape as useHolidays. Composes the
// shared SWR trigger core (lib/useFetchTrigger) — replacing a drifted
// `nonce > 0 ? { cache: 'reload' } : {}` under which one reload() made every
// later fetch bypass the cache forever while tick revalidations never did.
export function usePayMarks(from: string, to: string): { marks: PayMark[]; reload: () => void } {
  const [marks, setMarks] = useState<PayMark[]>([]);
  const trigger = useFetchTrigger();

  useEffect(() => {
    const run = trigger.begin();
    api<PayMark[]>(`/pay-marks?from=${from}&to=${to}`, run.init)
      .then((body) => {
        if (!run.isCurrent()) return;
        run.markContent();
        setMarks(body.data ?? []);
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) return;
      });
    return run.cancel;
  }, [from, to, trigger.nonce, trigger.tick]);

  return { marks, reload: trigger.reload };
}
